import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AppRoutes } from "./AppRoutes";
import { trackPageView } from "./utils/analytics";
import { reportOutreachVisit } from "./utils/reportOutreachVisit";
import { ServiceListProvider } from "./contexts/ServiceListContext";
import { MewsConfigurationProvider } from "./contexts/MewsConfigurationContext";

const queryClient = new QueryClient();

/**
 * Analytics tracker component for SPA navigation
 * Tracks page views on route changes for GA4
 */
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, document.title);

    // Scroll to top on route change if no hash
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    reportOutreachVisit(location.search);
  }, [location.pathname, location.search]);

  return null;
};

/**
 * Handles scrolling to hash fragments on page load or navigation
 */
const ScrollToHash = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        // Delay slightly to ensure content is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    }
  }, [hash]);

  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <MewsConfigurationProvider>
          <ServiceListProvider>
            <TooltipProvider>
              <div className="mobile-no-horizontal-scroll mobile-text-optimized">
                <Toaster />
                <Sonner />
                <BrowserRouter
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <AnalyticsTracker />
                  <ScrollToHash />
                  <AppRoutes />
                </BrowserRouter>
              </div>
            </TooltipProvider>
          </ServiceListProvider>
        </MewsConfigurationProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
